import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import DirectionsRunRoundedIcon from '@mui/icons-material/DirectionsRunRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import GetAppIcon from '@mui/icons-material/GetApp';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../../api';
import { groupesMusculaires, getSeanceTypeLabel, normalizeSeanceType, seanceTypeOptions } from './utils';
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts';
import { useNavigate, useSearchParams } from 'react-router-dom';

const typeStyles = {
  'SANTÉ': {
    label: 'SANTÉ',
    icon: <FavoriteRoundedIcon fontSize="small" />,
    bg: '#ECF8F0',
    softBg: '#F6FCF8',
    border: '#CAE8D4',
    color: '#2E7D4F',
    detailBg: '#F8FCF9'
  },
  'ESTHETIQUE': {
    label: 'ESTHÉTIQUE',
    icon: <AutoAwesomeRoundedIcon fontSize="small" />,
    bg: '#F6EEFB',
    softBg: '#FBF7FE',
    border: '#E3D1F1',
    color: '#8554A1',
    detailBg: '#FCFAFE'
  },
  'SPORT': {
    label: 'SPORT',
    icon: <DirectionsRunRoundedIcon fontSize="small" />,
    bg: '#EDF5FF',
    softBg: '#F7FBFF',
    border: '#CFE1FF',
    color: '#2B6CB0',
    detailBg: '#FAFCFF'
  }
};

const defaultTypeStyle = {
  label: 'SÉANCE',
  icon: <ExpandMoreIcon fontSize="small" />,
  bg: '#F4F7FB',
  softBg: '#FAFBFD',
  border: '#E2E8F0',
  color: '#405066',
  detailBg: '#FCFDFE'
};

export default function MonCarnet({ eleveId: propEleveId, readOnly = false }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const paramEleve = searchParams.get('eleve');
  const seanceTokenQuery = searchParams.get('seanceToken');

  const [eleveId, setEleveId] = useState(propEleveId || paramEleve || '');
  const [eleveInfo, setEleveInfo] = useState(null);
  const [loadingEleveInfo, setLoadingEleveInfo] = useState(false);
  const [seances, setSeances] = useState([]);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [groupeFilter, setGroupeFilter] = useState('');
  const [atelierFilter, setAtelierFilter] = useState('');
  const [openRows, setOpenRows] = useState({});

  useEffect(() => {
    if (propEleveId) setEleveId(propEleveId);
    if (paramEleve && !propEleveId) setEleveId(paramEleve);
  }, [propEleveId, paramEleve]);

  useEffect(() => {
    const fetchEleve = async () => {
      setEleveInfo(null);
      if (!eleveId && !seanceTokenQuery) return;

      setLoadingEleveInfo(true);
      try {
        if (seanceTokenQuery) {
          const res = await api.get(`/muscu/token-info?seanceToken=${encodeURIComponent(seanceTokenQuery)}`);
          if (res.data && res.data.eleve) {
            setEleveInfo(res.data.eleve);
            if (!eleveId) setEleveId(res.data.eleve._id);
          } else {
            setEleveInfo(null);
          }
        } else if (eleveId) {
          const res = await api.get(`/eleves/${eleveId}`);
          setEleveInfo(res.data || null);
        }
      } catch (err) {
        console.error('Erreur récupération élève MonCarnet', err);
        setEleveInfo(null);
      } finally {
        setLoadingEleveInfo(false);
      }
    };

    fetchEleve();
  }, [eleveId, seanceTokenQuery]);

  const fetchSeances = useCallback(async () => {
    if (!eleveId) return;
    try {
      const config = seanceTokenQuery ? { headers: { 'x-seance-token': seanceTokenQuery } } : {};
      const res = await api.get(`/muscu/seances/${eleveId}`, config);
      setSeances(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erreur fetch seances', err);
      setSeances([]);
    }
  }, [eleveId, seanceTokenQuery]);

  useEffect(() => {
    if (eleveId) fetchSeances();
  }, [eleveId, fetchSeances]);

  const resetFilters = () => {
    setFromDate('');
    setToDate('');
    setTypeFilter('');
    setGroupeFilter('');
    setAtelierFilter('');
  };

  const toggleRow = (id) => {
    setOpenRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const allAteliers = useMemo(() => {
    const setA = new Set();
    seances.forEach((s) => {
      (s.ateliers || []).forEach((a) => {
        if (a.atelierNom) setA.add(a.atelierNom);
      });
    });
    return Array.from(setA).sort();
  }, [seances]);

  const filtered = useMemo(() => {
    return seances.filter((s) => {
      const date = new Date(s.date);
      if (fromDate && date < new Date(fromDate)) return false;
      if (toDate && date > new Date(toDate)) return false;
      if (typeFilter && normalizeSeanceType(s.type) !== normalizeSeanceType(typeFilter)) return false;
      if (groupeFilter) {
        const match = (s.ateliers || []).some((a) => (a.groupe || '').toLowerCase().includes(groupeFilter.toLowerCase()));
        if (!match) return false;
      }
      if (atelierFilter) {
        const match = (s.ateliers || []).some((a) => (a.atelierNom || '').toLowerCase().includes(atelierFilter.toLowerCase()));
        if (!match) return false;
      }
      return true;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [seances, fromDate, toDate, typeFilter, groupeFilter, atelierFilter]);

  const exportCSV = () => {
    if (!eleveId) return;
    const rows = [];
    rows.push(['date', 'type', 'atelier', 'groupe', 'series', 'repetitions', 'poids', 'ressenti']);
    filtered.forEach((s) => {
      (s.ateliers || []).forEach((a) => {
        rows.push([
          new Date(s.date).toISOString(),
          getSeanceTypeLabel(s.type),
          a.atelierNom || '',
          a.groupe || '',
          a.series ?? '',
          a.repetitions ?? '',
          a.poids ?? '',
          a.ressenti ?? ''
        ]);
      });
    });
    const csv = rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `carnet_${eleveId || 'eleve'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    if (!readOnly || !filtered.length) return;

    const title = eleveInfo ? `${eleveInfo.prenom} ${eleveInfo.nom}` : 'Carnet muscu';
    const sections = filtered.map((seance) => {
      const typeMeta = getTypeStyle(seance.type);
      const ateliers = (seance.ateliers || []).map((atelier) => `
        <tr>
          <td>${atelier.groupe || '-'}</td>
          <td>${atelier.atelierNom || '-'}</td>
          <td>${atelier.series ?? '-'}</td>
          <td>${atelier.repetitions ?? '-'}</td>
          <td>${atelier.poids || '-'}</td>
          <td>${atelier.ressenti ?? '-'}</td>
        </tr>
      `).join('');

      return `
        <section style="border:1px solid ${typeMeta.border}; border-radius:16px; padding:16px; margin-bottom:16px; background:${typeMeta.softBg}; page-break-inside:avoid;">
          <div style="display:flex; justify-content:space-between; align-items:center; gap:12px; margin-bottom:12px;">
            <div>
              <div style="font-size:18px; font-weight:700; color:#1f2937;">${new Date(seance.date).toLocaleDateString()}</div>
              <div style="display:inline-block; margin-top:6px; padding:6px 12px; border-radius:999px; background:${typeMeta.bg}; color:${typeMeta.color}; font-weight:700; font-size:12px;">${typeMeta.label}</div>
            </div>
            <div style="font-size:13px; color:#475569; font-weight:600;">${(seance.ateliers || []).length} atelier${(seance.ateliers || []).length > 1 ? 's' : ''}</div>
          </div>
          <table style="width:100%; border-collapse:collapse; font-size:12px;">
            <thead>
              <tr style="background:#ffffff;">
                <th style="text-align:left; padding:8px; border:1px solid #d9e2ec;">Groupe</th>
                <th style="text-align:left; padding:8px; border:1px solid #d9e2ec;">Atelier</th>
                <th style="text-align:left; padding:8px; border:1px solid #d9e2ec;">Séries</th>
                <th style="text-align:left; padding:8px; border:1px solid #d9e2ec;">Répétitions</th>
                <th style="text-align:left; padding:8px; border:1px solid #d9e2ec;">Poids</th>
                <th style="text-align:left; padding:8px; border:1px solid #d9e2ec;">Ressenti</th>
              </tr>
            </thead>
            <tbody>${ateliers}</tbody>
          </table>
        </section>
      `;
    }).join('');

    const newWin = window.open('', '_blank', 'width=900,height=1000');
    if (!newWin) return;
    newWin.document.write(`
      <html>
        <head>
          <title>Carnet muscu - ${title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #1f2937; }
            h1 { margin: 0 0 8px; }
            p { margin: 0 0 24px; color: #475569; }
            td { padding: 8px; border: 1px solid #d9e2ec; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <p>Synthèse du carnet muscu exportée en PDF.</p>
          ${sections}
        </body>
      </html>
    `);
    newWin.document.close();
    newWin.focus();
    setTimeout(() => newWin.print(), 250);
  };

  const handleDeleteSeance = async (id) => {
    if (!window.confirm('Supprimer cette séance ?')) return;
    try {
      const config = seanceTokenQuery ? { headers: { 'x-seance-token': seanceTokenQuery } } : {};
      await api.delete(`/muscu/seances/${id}`, config);
      setSeances((prev) => prev.filter((s) => s._id !== id));
    } catch (err) {
      console.error('Erreur suppression séance', err);
      alert('Erreur suppression séance');
    }
  };

  const handleDeleteAtelier = async (seanceId, atelierIndex) => {
    if (!window.confirm('Supprimer cet atelier de la séance ?')) return;
    try {
      const seance = seances.find((s) => s._id === seanceId);
      if (!seance) return;
      const newAteliers = (seance.ateliers || []).filter((_, i) => i !== atelierIndex);
      const config = seanceTokenQuery ? { headers: { 'x-seance-token': seanceTokenQuery } } : {};
      await api.put(`/muscu/seances/${seanceId}`, { ateliers: newAteliers }, config);
      setSeances((prev) => prev.map((s) => (s._id === seanceId ? { ...s, ateliers: newAteliers } : s)));
    } catch (err) {
      console.error('Erreur suppression atelier', err);
      alert('Erreur suppression atelier (vérifier que le backend accepte la mise à jour de séance)');
    }
  };

  const chartData = useMemo(() => {
    if (!atelierFilter) return [];
    const points = [];
    seances.forEach((s) => {
      const at = (s.ateliers || []).find((a) => a.atelierNom === atelierFilter);
      if (at) points.push({ date: new Date(s.date).toLocaleDateString(), ressenti: at.ressenti ?? 0, rawDate: s.date });
    });
    return points.sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate)).map((p) => ({ date: p.date, ressenti: p.ressenti }));
  }, [seances, atelierFilter]);

  const goToAddSeance = () => {
    const params = new URLSearchParams();
    if (eleveId) params.set('eleve', eleveId);
    if (seanceTokenQuery) params.set('seanceToken', seanceTokenQuery);
    const q = params.toString();
    navigate(`/carnet-muscu/ajouter${q ? `?${q}` : ''}`);
  };

  const getTypeStyle = (type) => typeStyles[normalizeSeanceType(type)] || defaultTypeStyle;

  const ressentiMap = {
    1: { emoji: '😎', label: 'Trop facile' },
    2: { emoji: '🤗', label: 'Facile' },
    3: { emoji: '☺️', label: 'Moyen' },
    4: { emoji: '😅', label: 'Difficile' },
    5: { emoji: '🥵', label: 'Impossible' }
  };

  const renderRessentiCell = (val) => {
    if (val === undefined || val === null) return '-';
    const r = ressentiMap[val] || null;
    if (!r) return val;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{ fontSize: 18 }}>{r.emoji}</Box>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{r.label}</Typography>
      </Box>
    );
  };

  const renderTypeChip = (type) => {
    const meta = getTypeStyle(type);
    return (
      <Chip
        icon={meta.icon}
        label={meta.label}
        size="small"
        sx={{
          fontWeight: 800,
          backgroundColor: meta.bg,
          color: meta.color,
          border: `1px solid ${meta.border}`,
          '& .MuiChip-icon': { color: meta.color }
        }}
      />
    );
  };

  const renderToggleButton = (seanceId) => {
    const isOpen = !!openRows[seanceId];
    return (
      <Button
        variant="contained"
        size={isMobile ? 'medium' : 'small'}
        onClick={() => toggleRow(seanceId)}
        startIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{
          minWidth: isMobile ? '100%' : 210,
          borderRadius: 999,
          px: isMobile ? 2.25 : 2.5,
          py: isMobile ? 0.65 : 0.6,
          minHeight: isMobile ? 38 : 34,
          whiteSpace: 'nowrap',
          fontWeight: 800,
          fontSize: isMobile ? '0.8rem' : '0.74rem',
          letterSpacing: 0.2,
          textTransform: 'uppercase',
          backgroundColor: isOpen ? '#0B5E2F' : '#1565C0',
          color: '#fff',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: isOpen ? '#084A25' : '#0D47A1',
            boxShadow: 'none'
          }
        }}
      >
        {isOpen ? 'Masquer le détail' : 'Voir le détail'}
      </Button>
    );
  };

  const renderSeanceDetailContent = (seance) => {
    const typeMeta = getTypeStyle(seance.type);
    return (
      <Box sx={{ p: isMobile ? 1.5 : 2, bgcolor: typeMeta.detailBg, borderTop: `1px solid ${typeMeta.border}`, borderBottom: `1px solid ${typeMeta.border}` }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 800, color: typeMeta.color }}>
          Détails des ateliers
        </Typography>
        {(seance.ateliers || []).length === 0 ? (
          <Typography variant="body2">Aucun atelier</Typography>
        ) : isMobile ? (
          <Stack spacing={1}>
            {(seance.ateliers || []).map((atelier, idx) => (
              <Paper
                key={idx}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  backgroundColor: '#fff',
                  border: `1px solid ${typeMeta.border}`
                }}
              >
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>
                        {atelier.atelierNom || '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {atelier.groupe || 'Groupe non renseigné'}
                      </Typography>
                    </Box>
                    <Box>{renderRessentiCell(atelier.ressenti)}</Box>
                  </Stack>

                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <Box sx={{ p: 1, borderRadius: 2, bgcolor: typeMeta.softBg, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Séries</Typography>
                        <Typography sx={{ fontWeight: 800 }}>{atelier.series ?? '-'}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ p: 1, borderRadius: 2, bgcolor: typeMeta.softBg, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Répét.</Typography>
                        <Typography sx={{ fontWeight: 800 }}>{atelier.repetitions ?? '-'}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ p: 1, borderRadius: 2, bgcolor: typeMeta.softBg, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Poids</Typography>
                        <Typography sx={{ fontWeight: 800 }}>{atelier.poids || '-'}</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {!readOnly && (
                    <Button
                      color="error"
                      variant="outlined"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteAtelier(seance._id, idx)}
                      sx={{ alignSelf: 'stretch', borderRadius: 999 }}
                    >
                      Supprimer cet atelier
                    </Button>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Groupe</TableCell>
                <TableCell>Atelier</TableCell>
                <TableCell>Séries</TableCell>
                <TableCell>Répétitions</TableCell>
                <TableCell>Poids/Distance</TableCell>
                <TableCell>Ressenti</TableCell>
                {!readOnly && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {(seance.ateliers || []).map((atelier, idx) => (
                <TableRow key={idx}>
                  <TableCell>{atelier.groupe || '-'}</TableCell>
                  <TableCell>{atelier.atelierNom || '-'}</TableCell>
                  <TableCell>{atelier.series ?? '-'}</TableCell>
                  <TableCell>{atelier.repetitions ?? '-'}</TableCell>
                  <TableCell>{atelier.poids || '-'}</TableCell>
                  <TableCell>{renderRessentiCell(atelier.ressenti)}</TableCell>
                  {!readOnly && (
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => handleDeleteAtelier(seance._id, idx)} title="Supprimer atelier">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    );
  };

  const renderDesktopTable = () => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Ateliers</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {filtered.map((seance) => {
            const isOpen = !!openRows[seance._id];
            const typeMeta = getTypeStyle(seance.type);
            return (
              <React.Fragment key={seance._id}>
                <TableRow sx={{ backgroundColor: isOpen ? typeMeta.bg : typeMeta.softBg }}>
                  <TableCell sx={{ fontWeight: 700 }}>{new Date(seance.date).toLocaleDateString()}</TableCell>
                  <TableCell>{renderTypeChip(seance.type)}</TableCell>
                  <TableCell>{(seance.ateliers || []).map((a) => a.atelierNom).join(', ') || '-'}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      {renderToggleButton(seance._id)}
                      {!readOnly && (
                        <IconButton size="small" color="error" onClick={() => handleDeleteSeance(seance._id)} title="Supprimer"><DeleteIcon /></IconButton>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell colSpan={4} sx={{ p: 0, border: 0 }}>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      {renderSeanceDetailContent(seance)}
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
          {filtered.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} align="center">Aucune séance trouvée</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderMobileCards = () => (
    <Stack spacing={1.1}>
      {filtered.length === 0 && (
        <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E4EBF3', textAlign: 'center' }}>
          <Typography>Aucune séance trouvée</Typography>
        </Paper>
      )}

      {filtered.map((seance) => {
        const isOpen = !!openRows[seance._id];
        const typeMeta = getTypeStyle(seance.type);
        return (
          <Paper
            key={seance._id}
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              border: `1px solid ${typeMeta.border}`,
              backgroundColor: typeMeta.softBg,
              boxShadow: isOpen ? `0 8px 24px ${typeMeta.border}` : '0 6px 18px rgba(32,24,64,0.06)'
            }}
          >
            <Box sx={{ p: 1.5 }}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.98rem', lineHeight: 1.25 }}>
                      {new Date(seance.date).toLocaleDateString()}
                    </Typography>
                    <Box sx={{ mt: 0.75 }}>{renderTypeChip(seance.type)}</Box>
                  </Box>
                  <Chip label={`${(seance.ateliers || []).length} atelier${(seance.ateliers || []).length > 1 ? 's' : ''}`} size="small" sx={{ fontWeight: 700, backgroundColor: '#fff' }} />
                </Stack>

                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                    Exercices
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.25 }}>
                    {(seance.ateliers || []).map((a) => a.atelierNom).join(', ') || '-'}
                  </Typography>
                </Box>

                <Stack spacing={1}>
                  {renderToggleButton(seance._id)}
                  {!readOnly && (
                    <Button
                      color="error"
                      variant="outlined"
                      fullWidth
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteSeance(seance._id)}
                      sx={{ borderRadius: 999 }}
                    >
                      Supprimer
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Box>

            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              {renderSeanceDetailContent(seance)}
            </Collapse>
          </Paper>
        );
      })}
    </Stack>
  );

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 4 }, borderRadius: { xs: 3, sm: 4 } }}>
      <Stack spacing={{ xs: 2, sm: 2.5 }}>
        <Grid container spacing={1.5} alignItems="center" justifyContent="space-between">
          <Grid item xs={12} sm="auto">
            <Typography variant="h6" sx={{ fontWeight: 800, textAlign: { xs: 'center', sm: 'left' } }}>
              {readOnly ? 'SUIVI MUSCU DE L’ÉLÈVE' : 'MON SUIVI MUSCU'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'center', sm: 'flex-end' }} flexWrap="wrap" useFlexGap>
              <IconButton onClick={exportCSV} title="Exporter CSV" sx={{ backgroundColor: '#F4F7FB' }}>
                <GetAppIcon />
              </IconButton>
              {readOnly && (
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdfRoundedIcon />}
                  onClick={exportPDF}
                  sx={{
                    backgroundColor: '#FFE7EA',
                    color: '#A61B34',
                    textTransform: 'none',
                    borderRadius: 999,
                    px: 2,
                    '&:hover': { backgroundColor: '#FFD6DD' }
                  }}
                >
                  Exporter en PDF
                </Button>
              )}
              {!readOnly && (
                <Button
                  variant="contained"
                  onClick={goToAddSeance}
                  sx={{
                    backgroundColor: '#DFF7E6',
                    color: '#0B5E2F',
                    textTransform: 'none',
                    borderRadius: 999,
                    px: 2,
                    '&:hover': { backgroundColor: '#C9F1D1' }
                  }}
                >
                  NOUVELLE SÉANCE
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>

        <Box>
          {!eleveId ? (
            <TextField
              label="Eleve ID"
              value={eleveId}
              onChange={(e) => setEleveId(e.target.value)}
              helperText="Saisir l'ID élève pour charger les séances"
            />
          ) : loadingEleveInfo ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Chargement élève...</Typography>
            </Box>
          ) : eleveInfo ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box component="img" src={`${process.env.PUBLIC_URL}/assets/graphique.png`} alt="graph" sx={{ width: { xs: 84, sm: 120 }, height: { xs: 84, sm: 120 }, mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center', mt: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                {eleveInfo.prenom} {eleveInfo.nom}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2">Élève : <span style={{ fontWeight: 'bold' }}>{eleveId}</span></Typography>
          )}
        </Box>

        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              mb: 1.25,
              fontWeight: 900,
              color: '#0D47A1',
              letterSpacing: 0.2,
              textTransform: 'uppercase'
            }}
          >
            Filtrer
          </Typography>
          <Box
            component="form"
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit,minmax(140px,1fr))' },
              gap: 1.5
            }}
          >
            <TextField label="Date début" type="date" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            <TextField label="Date fin" type="date" InputLabelProps={{ shrink: true }} value={toDate} onChange={(e) => setToDate(e.target.value)} />

            <FormControl>
              <InputLabel id="type-filter-label">Type de séance</InputLabel>
              <Select
                labelId="type-filter-label"
                value={typeFilter}
                label="Type de séance"
                onChange={(e) => setTypeFilter(normalizeSeanceType(e.target.value))}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) return 'Tous';
                  const option = seanceTypeOptions.find(({ value }) => value === selected);
                  return option?.label || getSeanceTypeLabel(selected);
                }}
              >
                <MenuItem value="">Tous</MenuItem>
                {seanceTypeOptions.map((option) => {
                  const typeMeta = getTypeStyle(option.value);
                  return (
                    <MenuItem key={option.value} value={option.value}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {typeMeta.icon}
                        <span>{option.label}</span>
                      </Stack>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <FormControl>
              <InputLabel id="groupe-filter-label">Muscles</InputLabel>
              <Select labelId="groupe-filter-label" value={groupeFilter} label="Muscles" onChange={(e) => setGroupeFilter(e.target.value)}>
                <MenuItem value="">Tous</MenuItem>
                {groupesMusculaires.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl>
              <InputLabel id="atelier-filter-label">Atelier</InputLabel>
              <Select labelId="atelier-filter-label" value={atelierFilter} label="Atelier" onChange={(e) => setAtelierFilter(e.target.value)}>
                <MenuItem value="">Tous</MenuItem>
                {allAteliers.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                fullWidth={isMobile}
                onClick={() => { resetFilters(); fetchSeances(); }}
                sx={{
                  backgroundColor: '#FFF4E6',
                  color: '#7A4300',
                  textTransform: 'none',
                  borderRadius: 999,
                  '&:hover': { backgroundColor: '#FFE8CC' }
                }}
              >
                RÉINITIALISER
              </Button>

              <IconButton
                onClick={fetchSeances}
                title="Recharger"
                sx={{
                  backgroundColor: '#E6F2FF',
                  color: '#003A66',
                  borderRadius: 999,
                  '&:hover': { backgroundColor: '#D0E9FF' }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Stack>
          </Box>
        </Box>

        {atelierFilter && chartData.length > 0 && (
          <Box sx={{ height: { xs: 220, sm: 250 } }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
              Évolution du ressenti pour "{atelierFilter}"
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis domain={[0, 5]} />
                <Tooltip />
                <Line type="monotone" dataKey="ressenti" stroke="#8884d8" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}

        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            borderRadius: 3,
            backgroundColor: '#EDF4FF',
            border: '1px solid #CFE0FF'
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <ExpandMoreIcon sx={{ color: '#1565C0' }} />
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0D47A1' }}>
              Utilise le bouton bleu « Voir le détail » pour afficher tous les exercices de chaque séance.
            </Typography>
          </Stack>
        </Box>

        {isMobile ? renderMobileCards() : renderDesktopTable()}
      </Stack>
    </Paper>
  );
}